import type { Language } from '@eformulieren/engine'
import { type AdviesEindreden, isOpenAdvies } from '@eformulieren/model'
import type { AdviceRecord, ReadAdviceFull } from './advice.js'
import type { Bilingual } from '@eformulieren/renderer'

/**
 * Welke stap de adviseurpagina toont (R-184, R-184c, R-184d, R-184g, R-184h, besluit van de gebruiker
 * van 30 oktober 2026, ontwerp 7.4a).
 *
 *   - `verklaren`: het verzoek loopt en er is nog geen verklaring. De pagina toont alleen de vraag, het
 *     anker en de partijen (`InterestDeclaration`), en vraagt niets anders op: `/projectie` en
 *     `/stand` geven tot "geen belang" toch 404.
 *   - `lezen`: geen belang, verklaard of door NWO vastgesteld, en het verzoek loopt. Heeft NWO een
 *     gemeld belang tegengesproken, dan leest de adviseur dat in zijn paneel (`AdviserPanel`). Dan het formulier met alleen de gedeelde
 *     secties, alleen lezen, en het antwoordpaneel.
 *   - `belang`: de adviseur meldde een belang, of NWO stelde er een vast (R-184k); het verzoek is
 *     geëindigd. `door` zegt wie, en bij NWO staat de toelichting van NWO erbij.
 *   - `beeindigd`: afgerond, ingetrokken, vervallen, of de beurt sloot; de toegang is voorbij.
 *   - `geen`: deze persoon is hier geen adviseur, of de pod antwoordde niet.
 *
 * **Meer verzoeken aan dezelfde adviseur op één instantie** (dat kan: twee vragen over twee secties):
 * een verzoek dat loopt gaat voor een geëindigd, en daarbinnen het eerste in de volgorde van de pod.
 * De andere komen aan bod zodra dit verzoek eindigt.
 */

export type AdviserStep =
  | { readonly soort: 'verklaren'; readonly verzoek: AdviceRecord }
  | { readonly soort: 'lezen'; readonly verzoek: AdviceRecord }
  | { readonly soort: 'belang'; readonly door: 'adviseur' | 'nwo'; readonly toelichting?: string }
  | { readonly soort: 'beeindigd'; readonly reden: AdviesEindreden }
  | { readonly soort: 'geen' }

export function adviserStep(read: ReadAdviceFull | undefined): AdviserStep {
  const mine = read?.verzoeken.filter((request) => request.mijnRol === 'adviseur') ?? []
  const request = mine.find((candidate) => isOpenAdvies(candidate.toestand)) ?? mine[0]
  if (request === undefined) return { soort: 'geen' }
  // Wat geldt: het laatste oordeel van NWO, anders de eigen verklaring (R-184k).
  const judgement = request.nwoOordelen?.at(-1)
  const outcome = judgement?.uitkomst ?? request.belangenverklaring?.uitkomst
  if (request.toestand === 'belang-gemeld') {
    return judgement?.uitkomst === 'belang' ? { soort: 'belang', door: 'nwo', toelichting: judgement.toelichting } : { soort: 'belang', door: 'adviseur' }
  }
  if (!isOpenAdvies(request.toestand)) return { soort: 'beeindigd', reden: request.eindreden ?? 'afgerond' }
  if (outcome === 'geen-belang') return { soort: 'lezen', verzoek: request }
  return { soort: 'verklaren', verzoek: request }
}

const ENDED: Bilingual = { nl: 'Uw toegang is beëindigd', en: 'Your access has ended' }
const INTEREST: Bilingual = { nl: 'U hebt een belang gemeld', en: 'You have declared an interest' }
const READ_NO_MORE: Bilingual = {
  nl: 'U kunt dit formulier niet meer lezen en niet meer antwoorden. Wat u eerder schreef, blijft bij het dossier.',
  en: 'You can no longer read this form or answer. What you wrote earlier stays with the file.',
}

const REASON: Readonly<Record<AdviesEindreden, Bilingual>> = {
  afgerond: { nl: 'NWO heeft het advies afgerond. Dank voor uw advies.', en: 'NWO has completed the advice. Thank you for your advice.' },
  ingetrokken: { nl: 'NWO heeft de adviesvraag ingetrokken.', en: 'NWO has withdrawn the request for advice.' },
  'belang-gemeld': {
    nl: 'U hebt een belang gemeld; de vrager hoort het van NWO. Het verzoek is daarmee beëindigd en u krijgt geen toegang tot het formulier.',
    en: 'You have declared an interest; the person who asked hears it from NWO. The request has ended and you get no access to the form.',
  },
  'beurt-gesloten': {
    nl: 'De fase van het formulier waarin om advies is gevraagd, is afgesloten.',
    en: 'The phase of the form in which advice was asked has been closed.',
  },
  'uitnodiging-verlopen': { nl: 'De uitnodiging is verlopen.', en: 'The invitation has expired.' },
  'uitnodiging-geweigerd': { nl: 'De uitnodiging is geweigerd.', en: 'The invitation was declined.' },
  'uitnodiging-ingetrokken': { nl: 'NWO heeft de uitnodiging ingetrokken.', en: 'NWO has withdrawn the invitation.' },
  'toegang-deler-vervallen': {
    nl: 'De medewerker van NWO die om advies vroeg, heeft zelf geen toegang meer tot dit formulier.',
    en: 'The NWO employee who asked for advice no longer has access to this form.',
  },
}

const NWO_INTEREST: Bilingual = { nl: 'NWO heeft vastgesteld dat u een belang hebt', en: 'NWO has established that you have an interest' }
const NWO_INTEREST_WHY: Bilingual = {
  nl: 'NWO heeft uw belangenverklaring beoordeeld en vastgesteld dat u een belang hebt bij deze aanvraag. Het verzoek is daarmee beëindigd en u hebt geen toegang meer tot het formulier.',
  en: 'NWO has reviewed your declaration of interest and established that you have an interest in this application. The request has ended and you no longer have access to the form.',
}
const NWO_EXPLANATION: Bilingual = { nl: 'Toelichting van NWO: {t}', en: "NWO's explanation: {t}" }

/**
 * De eindpagina als NWO een belang vaststelde (R-184k): wat NWO besliste, met de toelichting van NWO,
 * en dat wat de adviseur eerder schreef bij het dossier blijft.
 */
export function judgedEndText(toelichting: string | undefined, language: Language): { readonly titel: string; readonly uitleg: string; readonly wat: string } {
  return {
    titel: NWO_INTEREST[language],
    uitleg: NWO_INTEREST_WHY[language],
    wat: toelichting === undefined ? READ_NO_MORE[language] : `${NWO_EXPLANATION[language].replace('{t}', toelichting)} ${READ_NO_MORE[language]}`,
  }
}

/** De tekst van de eindpagina: een titel, waarom, en wat dat betekent. */
export function endText(reason: AdviesEindreden, language: Language): { readonly titel: string; readonly uitleg: string; readonly wat: string } {
  if (reason === 'belang-gemeld') return { titel: INTEREST[language], uitleg: REASON[reason][language], wat: '' }
  return { titel: ENDED[language], uitleg: REASON[reason][language], wat: READ_NO_MORE[language] }
}
